import { useLocalization } from "@fluent/react";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useProductTypeContent } from "@lending/hooks/useProductTypeContent";
import { type ProductType } from "@lending/navigation";
import { PersonalLoanHeroSection } from "@lending/pages/Common/components/PersonalLoanHeroSection";
import AutoLoanSVG from "./components/ProductTypeButton/ProductIconSVGs/AutoLoan";
import CreditCardSVG from "./components/ProductTypeButton/ProductIconSVGs/CreditCard";
import PersonalLoanSVG from "./components/ProductTypeButton/ProductIconSVGs/PersonalLoan";
import { ProductTypeButton } from "./components/ProductTypeButton/ProductTypeButton";
import { useProductSelectionContainer } from "./hooks/useProductSelectionContainer";
import styles from "./ProductSelection.module.css";
import { useHeroBreakpoints } from "../../hooks/useHeroBreakpoints";
import { usePageTransition } from "../../hooks/usePageTransition";
import type { JSX } from "react";

function ProductSelection() {
  const {
    showPersonalLoanOption,
    showCreditCardOption,
    showAutoLoanOption,
    showLoanPortalOption,
    onClickProduct,
  } = useProductSelectionContainer();

  const { m } = useHeroBreakpoints();
  const { l10n } = useLocalization();
  const navigate = useNavigateWithSearch();
  const { transitionClass, exitThen } = usePageTransition({ enter: false });
  const productTypeContent = useProductTypeContent();

  const hasAnyProductsAvailable =
    showPersonalLoanOption || showCreditCardOption || showAutoLoanOption;
  if (!hasAnyProductsAvailable) {
    return <ErrorScreen />;
  }

  const productTypeProps: Record<
    ProductType,
    { icon: JSX.Element; name: string; subtext: string }
  > = {
    "auto-loan": {
      icon: <AutoLoanSVG />,
      name: productTypeContent["auto-loan"].name,
      subtext: productTypeContent["auto-loan"].productSelectionSubtext,
    },
    "credit-card": {
      icon: <CreditCardSVG />,
      name: productTypeContent["credit-card"].name,
      subtext: productTypeContent["credit-card"].productSelectionSubtext,
    },
    "personal-loan": {
      icon: <PersonalLoanSVG />,
      name: productTypeContent["personal-loan"].name,
      subtext: productTypeContent["personal-loan"].productSelectionSubtext,
    },
  };

  return (
    <>
      <PageLayout>
        <div className={styles.container}>
          <PersonalLoanHeroSection />
          <div className={`${styles.pageContent} ${transitionClass}`}>
            <div className={styles.buttonsContainer}>
              {showCreditCardOption && (
                <ProductTypeButton
                  {...productTypeProps["credit-card"]}
                  onClick={() => {
                    exitThen(() => onClickProduct("credit-card"));
                  }}
                />
              )}
              {showPersonalLoanOption && (
                <ProductTypeButton
                  {...productTypeProps["personal-loan"]}
                  onClick={() => {
                    exitThen(() => onClickProduct("personal-loan"));
                  }}
                />
              )}
              {showAutoLoanOption && (
                <ProductTypeButton
                  {...productTypeProps["auto-loan"]}
                  onClick={() => {
                    exitThen(() => onClickProduct("auto-loan"));
                  }}
                />
              )}
            </div>

            {showLoanPortalOption && (
              <button
                type="button"
                className={styles.continueApplicationLink}
                onClick={() => {
                  exitThen(() => {
                    void navigate("/portal/login");
                  });
                }}
              >
                {l10n.getString(
                  "product-selection-continue-application-link",
                  null,
                  "Application in progress? Continue here"
                )}
              </button>
            )}

            {!m && (
              <div className={styles.mobilePromotionalDetailsCard}>
                <ul className="list--reset fontSize--s">
                  <li className="margin--bottom--xs">
                    <i className="narmi-icon-check fontColor--theme--primary" />
                    <span className="margin--left--xxs fontWeight--semibold">
                      {l10n.getString(
                        "product-selection-bullet-1",
                        null,
                        "Apply in just a few minutes"
                      )}
                    </span>
                  </li>
                  <li className="margin--bottom--xs">
                    <i className="narmi-icon-check fontColor--theme--primary" />
                    <span className="margin--left--xxs fontWeight--semibold">
                      {l10n.getString(
                        "product-selection-bullet-2",
                        null,
                        "Get your decision instantly"
                      )}
                    </span>
                  </li>
                  <li>
                    <i className="narmi-icon-check fontColor--theme--primary" />
                    <span className="margin--left--xxs fontWeight--semibold">
                      {l10n.getString("product-selection-bullet-3", null, "No application fees")}
                    </span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </PageLayout>
    </>
  );
}

export { ProductSelection };
